import { formatDistance } from 'date-fns'

import StatusIndicator from '@/components/TailwindUI/StatusIndicator'
import useFeatures from '@/components/useFeatures'
import UserProfile from '@/components/UserProfile'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { WorkgridDatasource } from '@/pages/SpaceAppBuilder/utilities/interfaces'

type DataSourceProps = {
  id: string
  name: string
  dataSourceName: string
  iconUrl: string
  isAuthenticated: boolean
  datasource: WorkgridDatasource
  onSelect: (id: string, datasourceId?: string) => void
  readOnly: boolean
  appCount: number
}

export default function DataSource({
  datasource,
  iconUrl,
  isAuthenticated,
  dataSourceName,
  onSelect,
  appCount
}: DataSourceProps) {
  const dataSourceInstanceQuery = {
    data: datasource,
    isSuccess: true,
    isLoading: false
  }
  const lastModifiedDate =
    datasource.modifiedDate ?? dataSourceInstanceQuery?.data?.modifiedDate ?? dataSourceInstanceQuery?.data?.createdDate
  const createdBy = dataSourceInstanceQuery.data?.createdBy
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  return (
    <li>
      <button
        title={isAuthenticated ? dataSourceName : 'Re-authenticate datasource'}
        className="relative flex w-full cursor-pointer items-center justify-between gap-x-8 px-2 py-4 text-left hover:bg-zinc-50"
        onClick={() => onSelect(datasource.id, 'datasourceId' in datasource ? datasource.datasourceId : undefined)}
      >
        <div className="flex w-full items-center transition-all lg:w-2/3">
          <div className="flex items-center justify-between pr-12">
            <div className="flex w-96 items-center gap-3">
              <div className="rounded-lg border bg-white p-2">
                <img className="h-10 w-10 rounded-md" src={iconUrl} alt="data source icon" />
              </div>
              <div>
                <p className="text-xs text-zinc-500 line-clamp-1" title={dataSourceName}>
                  {dataSourceName}
                </p>
                <h3 className="mt-0.5 text-lg font-bold text-zinc-900 line-clamp-1" title={datasource.name}>
                  {datasource.name}
                </h3>
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-2/3 items-center justify-between gap-x-8 transition-all">
          <div className="flex w-full justify-center gap-x-2">
            <div className="fade-in hidden w-full flex-wrap items-center justify-center gap-y-2 lg:flex">
              <div className="fade-in flex justify-end">
                <div>
                  <h3 className="text-sm text-zinc-500">{agentsFlagOn ? 'Agents' : 'Apps'}</h3>
                  <p className="mt-0.5 text-lg font-bold text-zinc-700">{appCount}</p>
                </div>
              </div>
            </div>
            <div className="fade-in hidden w-full flex-wrap items-center gap-y-2 lg:flex">
              <UserProfile userId={createdBy} enabled={dataSourceInstanceQuery.isSuccess} />
              {lastModifiedDate && (
                <div className="fade-in ml-2 text-xs text-zinc-500 line-clamp-1">
                  updated{' '}
                  {formatDistance(new Date(lastModifiedDate), new Date(), {
                    addSuffix: true
                  })}
                </div>
              )}
            </div>
            <div className="fade-in hidden min-w-[100px] items-center justify-start text-left md:flex">
              <StatusIndicator
                label={
                  dataSourceInstanceQuery.isLoading ? (
                    <p className="animate-pulse text-zinc-400">Loading...</p>
                  ) : isAuthenticated ? (
                    <p className="text-zinc-600">Connected</p>
                  ) : (
                    <p className="animate-pulse font-semibold text-red-600">Disconnected</p>
                  )
                }
                variant={dataSourceInstanceQuery.isLoading ? 'default' : isAuthenticated ? 'success' : 'error'}
                ping={!isAuthenticated}
              />
            </div>
          </div>
        </div>
      </button>
    </li>
  )
}
